/* eslint-disable no-console */
import {
  ChildProfile,
  AdultProfile,
  calculateAgeWithMonths,
  DEFAULT_TIMEZONE,
} from '@yoyoclub/shared';
import { Camera, Shield, FileText } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import FollowedHostsTab from '../components/FollowedHostsTab';
import MyFavoritesTab from '../components/MyFavoritesTab';
import MySubmissionsTab from '../components/MySubmissionsTab';
import CancelBookingModal from '../components/booking/CancelBookingModal';
import TransferConfirmationModal from '../components/booking/TransferConfirmationModal';
import WaitlistTab from '../components/booking/WaitlistTab';
import ConsentFormModal, { ConsentType } from '../components/consent/ConsentFormModal';
import MyCoursesTab from '../components/course/MyCoursesTab';
import MyGiftCardsTab from '../components/giftCard/MyGiftCardsTab';
import AdultProfileFormModal from '../components/profiles/AdultProfileFormModal';
import ChildProfileFormModal from '../components/profiles/ChildProfileFormModal';
import MyReviewsTab from '../components/reviews/MyReviewsTab';
import ReviewEditorModal from '../components/reviews/ReviewEditorModal';
import MyRewardsTab from '../components/rewards/MyRewardsTab';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { useAuth } from '../hooks/useAuth';
import { dateLocale } from '../i18n/locale';
import { localize } from '../i18n/script';
import { bookingApi, UserBookingsResponse, UserTransfer } from '../services/bookingApi';
import { consentApi, UserConsents } from '../services/consentApi';
import { passApi, type UserPassDetail } from '../services/passApi';
import { childProfileApi, adultProfileApi } from '../services/profileApi';
import { reviewApi } from '../services/reviewApi';
import { storeCreditApi } from '../services/storeCreditApi';
import { getLocalizedTitle } from '../utils/activityTransform';
import {
  canCancelBooking,
  canTransferBooking,
  isCancelledStatus,
  isTransferredStatus,
} from '../utils/bookingStatus';
import { showHostMessageButton } from '../utils/hostMessages';

// Reviews are writable for 90 days after the attended session (PRD R4).
// Keep in sync with backend REVIEW_WINDOW_MS in activityReview.service.ts.
const REVIEW_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

function getStatusBadge(
  status: string,
  t: (key: string) => string
): { label: string; className: string } {
  const map: Record<string, { label: string; className: string }> = {
    CONFIRMED: {
      label: t('dashboard:bookings.statusConfirmed'),
      className: 'bg-primary-pale text-primary-dark',
    },
    CANCELLED: {
      label: t('dashboard:bookings.statusCancelled'),
      className: 'bg-brown-pale text-brown-dark',
    },
    REFUNDED: {
      label: t('dashboard:bookings.statusCancelled'),
      className: 'bg-brown-pale text-brown-dark',
    },
    CANCELLED_CREDIT: {
      label: t('dashboard:bookings.statusCancelled'),
      className: 'bg-brown-pale text-brown-dark',
    },
    CANCELLED_REFUND: {
      label: t('dashboard:bookings.statusCancelled'),
      className: 'bg-brown-pale text-brown-dark',
    },
    CANCELLED_PASS: {
      label: t('dashboard:bookings.statusCancelledPass'),
      className: 'bg-brown-pale text-brown-dark',
    },
    TRANSFERRED: {
      label: t('dashboard:bookings.statusTransferred'),
      className: 'bg-yellow-pale text-yellow-dark',
    },
    CANCELLED_BY_HOST: {
      label: t('dashboard:bookings.statusCancelledByHost'),
      className: 'bg-orange-100 text-orange-800',
    },
  };
  return map[status.toUpperCase()] || { label: status, className: 'bg-gray-100 text-gray-600' };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation(['dashboard', 'pass', 'hostFollow']);
  const { user, loading: authLoading, isAuthenticated, getToken } = useAuth();

  const validTabs = [
    'bookings',
    'passes',
    'courses',
    'waitlist',
    'rewards',
    'giftCards',
    'reviews',
    'favorites',
    'submissions',
    'follows',
    'family',
    'consent',
  ] as const;
  type TabType = (typeof validTabs)[number];
  const tabParam = searchParams.get('tab') as TabType | null;
  const initialTab = tabParam && validTabs.includes(tabParam) ? tabParam : 'bookings';

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Rejected-review count drives the "我的评论" tab badge (replaces moderation emails).
  const [reviewRejectedCount, setReviewRejectedCount] = useState(0);
  // Activities the user has already reviewed, so a booking shows "查看我的评论"
  // instead of "写评价" (writing/editing all happens in the "我的评论" tab).
  const [reviewedActivityIds, setReviewedActivityIds] = useState<Set<string>>(new Set());
  // Activity id whose review composer modal is open (write flow from a booking).
  const [writeReviewActivityId, setWriteReviewActivityId] = useState<string | null>(null);

  // Profile state
  const [childProfiles, setChildProfiles] = useState<ChildProfile[]>([]);
  const [adultProfiles, setAdultProfiles] = useState<AdultProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showChildForm, setShowChildForm] = useState(false);
  const [showAdultForm, setShowAdultForm] = useState(false);
  const [editingChildProfile, setEditingChildProfile] = useState<ChildProfile | null>(null);
  const [editingAdultProfile, setEditingAdultProfile] = useState<AdultProfile | null>(null);

  // Bookings state
  const [bookings, setBookings] = useState<UserBookingsResponse[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasLoadedBookings, setHasLoadedBookings] = useState(false);
  const [cancelBookingTarget, setCancelBookingTarget] = useState<UserBookingsResponse | null>(null);
  const [transferBooking, setTransferBooking] = useState<UserBookingsResponse | null>(null);
  const [transfers, setTransfers] = useState<UserTransfer[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Store credit state
  const [creditBalance, setCreditBalance] = useState<number>(0);

  // Consent state
  const [consents, setConsents] = useState<UserConsents | null>(null);
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [hasLoadedConsents, setHasLoadedConsents] = useState(false);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [consentFormType, setConsentFormType] = useState<ConsentType>('all');

  // Passes state
  const [passes, setPasses] = useState<UserPassDetail[]>([]);
  const [passesLoading, setPassesLoading] = useState(false);
  const [hasLoadedPasses, setHasLoadedPasses] = useState(false);
  const [refundTargetPass, setRefundTargetPass] = useState<UserPassDetail | null>(null);
  const [refunding, setRefunding] = useState(false);

  // Tab change handler
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    window.history.replaceState({}, '', `?${params.toString()}`);
  };

  // Fetch profiles
  const fetchProfiles = useCallback(async () => {
    if (!user?.userId) return;

    try {
      setLoading(true);
      setError(null);
      const [children, adults] = await Promise.all([
        childProfileApi.getUserProfiles(),
        adultProfileApi.getUserProfiles(),
      ]);
      setChildProfiles(children);
      setAdultProfiles(adults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    if (!user?.userId) return;

    try {
      setBookingsLoading(true);
      setBookingsError(null);
      const token = await getToken();
      if (!token) throw new Error('No access token available');
      const [data, transfersData] = await Promise.all([
        bookingApi.getUserBookings(token),
        bookingApi.getUserTransfers(token),
      ]);
      setBookings(data);
      setTransfers(transfersData);
    } catch (err) {
      setBookingsError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setHasLoadedBookings(true);
      setBookingsLoading(false);
    }
  }, [user?.userId, getToken]);

  // Cancel booking handler
  const handleCancelConfirm = async (refundMethod: 'STORE_CREDIT' | 'STRIPE_REFUND') => {
    if (!cancelBookingTarget) return;
    const token = await getToken();
    if (!token) throw new Error('No access token available');

    const result = await bookingApi.cancelBooking(cancelBookingTarget.id, token, refundMethod);

    // Update booking status in list (only update status fields, preserve session/activity data)
    setBookings((prev) =>
      prev.map((b) =>
        b.id === cancelBookingTarget.id
          ? {
              ...b,
              status: result.booking.status,
              storeCreditApplied: result.booking.storeCreditApplied,
              refundMethod: result.booking.refundMethod,
              stripeRefundAmount: result.booking.stripeRefundAmount,
            }
          : b
      )
    );
    setCancelBookingTarget(null);

    // Refresh credit balance
    try {
      const balance = await storeCreditApi.getBalance(token);
      setCreditBalance(balance);
    } catch {
      /* ignore */
    }

    setSuccessMessage(t('dashboard:bookings.cancelSuccess'));
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Transfer booking handler
  const handleTransferConfirm = async () => {
    if (!transferBooking) return;
    const token = await getToken();
    if (!token) throw new Error('No access token available');

    await bookingApi.initiateTransfer(transferBooking.id, token);

    const [bookingsData, transfersData] = await Promise.all([
      bookingApi.getUserBookings(token),
      bookingApi.getUserTransfers(token),
    ]);
    setBookings(bookingsData);
    setTransfers(transfersData);
    setTransferBooking(null);
    setSuccessMessage(t('dashboard:bookings.transferSuccess'));
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleGenerateCode = async (transferId: string) => {
    const token = await getToken();
    if (!token) throw new Error('No access token available');
    const result = await bookingApi.generateTransferCode(transferId, token);
    setTransfers((prev) =>
      prev.map((tr) => (tr.id === transferId ? { ...tr, transferCode: result.transferCode } : tr))
    );
  };

  const handleCopyLink = (transferCode: string, activitySlug: string) => {
    const url = `${window.location.origin}/activity/${activitySlug}?tc=${transferCode}`;
    navigator.clipboard.writeText(url);
    setCopiedCode(transferCode);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getTransferForBooking = (bookingId: string): UserTransfer | undefined => {
    return transfers.find((tr) => tr.bookingId === bookingId);
  };

  // Fetch store credit balance
  const fetchCreditBalance = useCallback(async () => {
    if (!user?.userId) return;
    try {
      const token = await getToken();
      if (!token) return;
      const balance = await storeCreditApi.getBalance(token);
      setCreditBalance(balance);
    } catch {
      /* ignore - non-critical */
    }
  }, [user?.userId, getToken]);

  // Fetch consents
  const fetchConsents = useCallback(async () => {
    if (!user?.userId) return;

    try {
      setConsentsLoading(true);
      const data = await consentApi.getUserConsents();
      setConsents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load consents');
    } finally {
      setHasLoadedConsents(true);
      setConsentsLoading(false);
    }
  }, [user?.userId]);

  // Fetch passes
  const fetchPasses = useCallback(async () => {
    if (!user?.userId) return;

    try {
      setPassesLoading(true);
      const token = await getToken();
      if (!token) throw new Error('No access token available');
      const data = await passApi.getUserPasses(token);
      setPasses(data);
    } catch (err) {
      console.error('Failed to load passes:', err);
    } finally {
      setHasLoadedPasses(true);
      setPassesLoading(false);
    }
  }, [user?.userId, getToken]);

  // Handle pass refund
  const handlePassRefund = async () => {
    if (!refundTargetPass) return;
    try {
      setRefunding(true);
      const token = await getToken();
      if (!token) throw new Error('No access token available');

      await passApi.refundUserPass(refundTargetPass.id, token);

      // Refresh passes and credit balance
      await Promise.all([fetchPasses(), fetchCreditBalance()]);
      setRefundTargetPass(null);

      const amount = (refundTargetPass.paymentAmount / 100).toFixed(2);
      setSuccessMessage(t('pass:dashboard.refundModal.success', { amount }));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refund pass');
    } finally {
      setRefunding(false);
    }
  };

  // Handle consent form submission
  const handleConsentSubmit = async (data: {
    photoVideoConsent: 'FULL' | 'BLURRED' | 'NO_PHOTO';
    liabilityWaiverAgreed: boolean;
    parentalReleaseAgreed: boolean;
  }) => {
    try {
      const updated = await consentApi.updateUserConsents(data);
      setConsents(updated);
      setSuccessMessage(t('dashboard:consent.saveSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update consents:', error);
      throw error;
    }
  };

  // Sync tab with URL on mount. validTabs is a stable readonly literal, so
  // omitting it from deps is safe — disable the lint rule explicitly.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (validTabs.includes(tab as TabType)) {
      setActiveTab(tab as TabType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch profiles and credit balance on mount
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchProfiles();
    fetchCreditBalance();
  }, [authLoading, isAuthenticated, navigate, fetchProfiles, fetchCreditBalance]);

  // Fetch bookings and credit balance when auth is ready or when switching to bookings tab
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user?.userId) return;

    // Fetch if we're on bookings tab and haven't loaded yet
    if (activeTab === 'bookings' && !hasLoadedBookings && !bookingsLoading) {
      fetchBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.userId, activeTab, hasLoadedBookings, bookingsLoading]);

  // Fetch consents when switching to consent tab
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user?.userId) return;

    if (activeTab === 'consent' && !hasLoadedConsents && !consentsLoading) {
      fetchConsents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.userId, activeTab, hasLoadedConsents, consentsLoading]);

  // Load the user's reviews: drives the tab badge (rejected count) and which
  // bookings already have a review. Re-run after writing one from a booking.
  const loadMyReviews = useCallback(async () => {
    try {
      const [res, picks] = await Promise.all([
        reviewApi.getMine(),
        // YOYO Picks reviews share the tab and its badge (PRD §5.5 P4, R18).
        reviewApi.pickMine().catch(() => ({ rejectedCount: 0 })),
      ]);
      setReviewRejectedCount(res.rejectedCount + picks.rejectedCount);
      setReviewedActivityIds(new Set(res.items.map((r) => r.activity.id)));
    } catch {
      setReviewRejectedCount(0);
      setReviewedActivityIds(new Set());
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user?.userId) return;
    loadMyReviews();
  }, [authLoading, isAuthenticated, user?.userId, activeTab, loadMyReviews]);

  // Fetch passes when switching to passes tab
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user?.userId) return;

    if (activeTab === 'passes' && !hasLoadedPasses && !passesLoading) {
      fetchPasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.userId, activeTab, hasLoadedPasses, passesLoading]);

  const handleDeleteChildProfile = async (profileId: string) => {
    if (!confirm(t('dashboard:family.children.deleteConfirm'))) return;

    try {
      await childProfileApi.deleteProfile(profileId);
      setChildProfiles(childProfiles.filter((p) => p.id !== profileId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete child profile');
    }
  };

  const handleDeleteAdultProfile = async (profileId: string) => {
    if (!confirm(t('dashboard:family.adults.deleteConfirm'))) return;

    try {
      await adultProfileApi.deleteProfile(profileId);
      setAdultProfiles(adultProfiles.filter((p) => p.id !== profileId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete adult profile');
    }
  };

  const handleEditChildProfile = (profile: ChildProfile) => {
    setEditingChildProfile(profile);
    setShowChildForm(true);
  };

  const handleEditAdultProfile = (profile: AdultProfile) => {
    setEditingAdultProfile(profile);
    setShowAdultForm(true);
  };

  const handleCloseChildForm = () => {
    setShowChildForm(false);
    setEditingChildProfile(null);
    fetchProfiles();
  };

  const handleCloseAdultForm = () => {
    setShowAdultForm(false);
    setEditingAdultProfile(null);
    fetchProfiles();
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('dashboard:loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('dashboard:title')}</h1>
            <p className="text-gray-600">{t('dashboard:subtitle')}</p>
          </div>
          {creditBalance > 0 && (
            <div className="p-4 bg-yellow-pale border border-yellow/30 rounded-lg text-right">
              <p className="text-sm text-brown font-medium">{t('dashboard:storeCredit.title')}</p>
              <p className="text-2xl font-bold text-brown-dark">
                ${(creditBalance / 100).toFixed(2)}
              </p>
            </div>
          )}
        </div>

        {/* Become a Host CTA — only shown to Guest users (not Host/Admin) */}
        {!user?.isHost && !user?.isAdmin && (
          <div className="mb-6 p-4 bg-primary-pale border border-primary/20 rounded-lg flex items-center justify-between">
            <div>
              <p className="font-medium text-primary-dark">{t('dashboard:becomeHost.title')}</p>
              <p className="text-sm text-primary-dark/70">{t('dashboard:becomeHost.subtitle')}</p>
            </div>
            <Button variant="primary" size="sm" onClick={() => navigate('/apply-host')}>
              {t('dashboard:becomeHost.cta')}
            </Button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-8">
          <div>
            {/* Horizontally scrollable on small screens so the tab labels never
                wrap or overflow; spreads out normally on wider viewports. */}
            <nav className="flex gap-6 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              {validTabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={`py-4 px-1 text-base font-semibold transition-colors relative whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab ? 'text-primary' : 'text-gray-600 hover:text-primary'
                  }`}
                >
                  {tab === 'reviews'
                    ? t('review:myTab')
                    : tab === 'follows'
                      ? t('hostFollow:manage.title')
                      : t(`dashboard:tabs.${tab}`)}
                  {tab === 'reviews' && reviewRejectedCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold text-white bg-red-500 rounded-full align-middle">
                      {reviewRejectedCount}
                    </span>
                  )}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* My Courses Tab Content */}
        {activeTab === 'courses' && (
          <div className="mb-8">
            <MyCoursesTab />
          </div>
        )}

        {/* Waitlist Tab Content */}
        {activeTab === 'waitlist' && (
          <div className="mb-8">
            <WaitlistTab />
          </div>
        )}

        {/* Reviews Tab Content */}
        {activeTab === 'reviews' && (
          <div className="mb-8">
            <MyReviewsTab onChanged={loadMyReviews} />
          </div>
        )}

        {/* Favorites Tab Content (PRD §5.9 F4) */}
        {activeTab === 'favorites' && (
          <div className="mb-8">
            <MyFavoritesTab />
          </div>
        )}

        {/* YOYO Picks suggestions (PRD §6.3, §8.4) */}
        {activeTab === 'submissions' && (
          <div className="mb-8">
            <MySubmissionsTab />
          </div>
        )}

        {/* Followed Hosts Tab Content */}
        {activeTab === 'follows' && (
          <div className="mb-8">
            <FollowedHostsTab />
          </div>
        )}

        {/* Member rewards (docs/prd-member-rewards.md §4) */}
        {activeTab === 'rewards' && (
          <div className="mb-8">
            <MyRewardsTab />
          </div>
        )}

        {/* My Gift Cards Tab Content */}
        {activeTab === 'giftCards' && (
          <div className="mb-8">
            <MyGiftCardsTab />
          </div>
        )}

        {/* Consent Tab Content */}
        {activeTab === 'consent' && (
          <div className="mb-8">
            {consentsLoading ? (
              <Card padding="lg">
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-gray-600">{t('dashboard:loading')}</p>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Photo & Video Consent */}
                <Card padding="lg" className="hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Camera className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-3">
                        {t('dashboard:consent.photoVideo.title')}
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setConsentFormType('photoVideo');
                          setShowConsentForm(true);
                        }}
                        className="w-full"
                      >
                        {consents?.photoVideoConsent
                          ? t('dashboard:consent.signed')
                          : t('dashboard:consent.viewAndSign')}
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Activity Waiver */}
                <Card padding="lg" className="hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-3">
                        {t('dashboard:consent.waiver.title')}
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setConsentFormType('waiver');
                          setShowConsentForm(true);
                        }}
                        className="w-full"
                      >
                        {consents?.liabilityWaiverAgreed
                          ? t('dashboard:consent.signed')
                          : t('dashboard:consent.viewAndSign')}
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Parent/Guardian Authorization */}
                <Card padding="lg" className="hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-3">
                        {t('dashboard:consent.authorization.title')}
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setConsentFormType('authorization');
                          setShowConsentForm(true);
                        }}
                        className="w-full"
                      >
                        {consents?.parentalReleaseAgreed
                          ? t('dashboard:consent.signed')
                          : t('dashboard:consent.viewAndSign')}
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab Content */}
        {activeTab === 'bookings' && (
          <div>
            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                {successMessage}
              </div>
            )}

            {/* Error Message */}
            {bookingsError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                {bookingsError}
              </div>
            )}

            {/* Loading State */}
            {bookingsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">{t('dashboard:loading')}</p>
              </div>
            ) : bookings.length === 0 ? (
              <Card padding="lg" className="text-center py-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('dashboard:bookings.noBookings')}
                </h2>
                <p className="text-gray-600 mb-6">
                  {t('dashboard:bookings.noBookingsDescription')}
                </p>
                <Button variant="primary" onClick={() => navigate('/activities')}>
                  {t('dashboard:bookings.exploreActivities')}
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => {
                  const badge = getStatusBadge(booking.status, t);
                  const cancelled = isCancelledStatus(booking.status);
                  const transferred = isTransferredStatus(booking.status);
                  const inactive = cancelled || transferred;
                  const transfer = transferred ? getTransferForBooking(booking.id) : undefined;

                  return (
                    <Card key={booking.id} padding="lg" className={inactive ? 'opacity-75' : ''}>
                      <div className="flex flex-col md:flex-row md:items-start gap-6">
                        {/* Activity Image */}
                        <div className="flex-shrink-0 w-full md:w-32 h-32 bg-gray-200 rounded-lg overflow-hidden">
                          {booking.session.activity.images?.[0]?.imageUrl ? (
                            <img
                              src={booking.session.activity.images[0].imageUrl}
                              alt={getLocalizedTitle(booking.session.activity, i18n.language)}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                              <span className="text-gray-500">No image</span>
                            </div>
                          )}
                        </div>

                        {/* Booking Details */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {getLocalizedTitle(booking.session.activity, i18n.language)}
                              </h3>
                              {(() => {
                                const sessionDesc = localize(
                                  booking.session.descriptionCN,
                                  booking.session.descriptionEN,
                                  i18n.language
                                );
                                return sessionDesc ? (
                                  <p className="text-sm text-gray-600 mt-1">{sessionDesc}</p>
                                ) : null;
                              })()}
                            </div>
                            {/* Status Badge */}
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </div>

                          {/* Session Info */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">
                                {t('dashboard:bookings.date')}
                              </p>
                              <p className="font-medium text-gray-900">
                                {new Date(booking.session.startTime).toLocaleDateString(
                                  dateLocale(i18n.language),
                                  { timeZone: DEFAULT_TIMEZONE }
                                )}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">
                                {t('dashboard:bookings.time')}
                              </p>
                              <p className="font-medium text-gray-900">
                                {new Date(booking.session.startTime).toLocaleTimeString(
                                  dateLocale(i18n.language),
                                  {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                    timeZone: DEFAULT_TIMEZONE,
                                  }
                                )}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">
                                {t('dashboard:bookings.participants')}
                              </p>
                              <p className="font-medium text-gray-900">
                                {booking.participants.length}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">
                                {t('dashboard:bookings.total')}
                              </p>
                              <p className="font-medium text-gray-900">
                                ${(booking.totalAmount / 100).toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {/* Host-cancelled banner */}
                          {booking.status.toUpperCase() === 'CANCELLED_BY_HOST' && (
                            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <p className="text-sm text-orange-800 font-medium">
                                {t('dashboard:bookings.hostCancelledBanner')}
                              </p>
                            </div>
                          )}

                          {/* Refund Info for cancelled bookings */}
                          {cancelled && booking.refundMethod && (
                            <div className="mb-4 p-2 bg-gray-50 rounded text-sm text-gray-600">
                              {booking.refundMethod === 'STORE_CREDIT'
                                ? t('dashboard:bookings.refundedToCredit', {
                                    amount: (booking.totalAmount / 100).toFixed(2),
                                  })
                                : booking.stripeRefundAmount
                                  ? t('dashboard:bookings.refundedToOriginal', {
                                      amount: (booking.stripeRefundAmount / 100).toFixed(2),
                                    })
                                  : null}
                            </div>
                          )}

                          {/* Transfer Info for transferred bookings */}
                          {transferred && transfer && (
                            <div className="mb-4 p-2 bg-yellow-50 rounded text-sm text-yellow-800">
                              <p>
                                {booking.totalAmount > 0
                                  ? t('dashboard:bookings.transferFillProgress', {
                                      filled: transfer.spotsFilled,
                                      total: transfer.spotsReleased,
                                      refunded: (transfer.refundedAmount / 100).toFixed(2),
                                    })
                                  : t('dashboard:bookings.transferFillProgressPass', {
                                      filled: transfer.spotsFilled,
                                      total: transfer.spotsReleased,
                                      restored: transfer.passSessionsRestored,
                                    })}
                              </p>
                              {transfer.status !== 'COMPLETED' && transfer.status !== 'EXPIRED' && (
                                <div className="mt-2">
                                  {transfer.transferCode ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        handleCopyLink(
                                          transfer.transferCode!,
                                          transfer.booking.session.activity.slug
                                        )
                                      }
                                    >
                                      {copiedCode === transfer.transferCode
                                        ? t('dashboard:bookings.linkCopied')
                                        : t('dashboard:bookings.copyTransferLink')}
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleGenerateCode(transfer.id)}
                                    >
                                      {t('dashboard:bookings.generateTransferLink')}
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Participants List */}
                          <div className="mb-4">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                              {t('dashboard:bookings.participantDetails')}
                            </p>
                            <div className="space-y-1">
                              {booking.participants.map((participant) => {
                                let name = 'Unknown';
                                if (participant.childProfile) {
                                  name =
                                    participant.childProfile.preferredName ||
                                    participant.childProfile.name;
                                } else if (participant.adultProfile) {
                                  name = participant.adultProfile.name;
                                }
                                return (
                                  <div key={participant.id} className="text-sm text-gray-700">
                                    {name}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col md:flex-row items-start md:items-center justify-end gap-2">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigate(`/activity/${booking.session.activity.slug}`)
                                }
                              >
                                {t('dashboard:bookings.viewActivity')}
                              </Button>
                              {showHostMessageButton(booking) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/bookings/${booking.id}/details`)}
                                >
                                  {t('dashboard:hostMessages.button')}
                                </Button>
                              )}
                              {booking.status.toUpperCase() === 'CONFIRMED' &&
                                new Date(booking.session.endTime) < new Date() &&
                                // Hosts can't review their own activity (admins still can),
                                // so hide the CTA that would otherwise dead-end for them.
                                (user?.isAdmin ||
                                  booking.session.activity.host?.userId !== booking.userId) &&
                                (reviewedActivityIds.has(booking.session.activity.id) ? (
                                  // Already reviewed: managing/viewing is never window-gated.
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleTabChange('reviews')}
                                    className="text-primary hover:text-primary-dark"
                                  >
                                    {t('review:viewMine')}
                                  </Button>
                                ) : new Date(booking.session.endTime).getTime() >
                                  Date.now() - REVIEW_WINDOW_MS ? (
                                  // Writable only within the 90-day window; past that the
                                  // backend rejects, so don't offer a dead-end CTA.
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      setWriteReviewActivityId(booking.session.activity.id)
                                    }
                                    className="text-primary hover:text-primary-dark"
                                  >
                                    {t('review:rateCta')}
                                  </Button>
                                ) : null)}
                              {canTransferBooking(booking) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setTransferBooking(booking)}
                                  className="text-yellow-700 hover:text-yellow-800"
                                >
                                  {t('dashboard:bookings.transfer')}
                                </Button>
                              )}
                              {canCancelBooking(booking) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCancelBookingTarget(booking)}
                                  className={
                                    booking.status.toUpperCase() === 'CANCELLED_BY_HOST'
                                      ? 'text-orange-700 hover:text-orange-800'
                                      : 'text-red-600 hover:text-red-700'
                                  }
                                >
                                  {booking.status.toUpperCase() === 'CANCELLED_BY_HOST'
                                    ? t('dashboard:bookings.chooseRefund')
                                    : t('dashboard:bookings.cancel')}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Family Tab - Profile Sections */}
        {activeTab === 'family' && (
          <>
            {/* Children Profiles Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('dashboard:family.children.title')}
                </h2>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setEditingChildProfile(null);
                    setShowChildForm(true);
                  }}
                >
                  + {t('dashboard:family.children.addChild')}
                </Button>
              </div>

              {loading ? (
                <Card padding="lg">
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('dashboard:loading')}</p>
                  </div>
                </Card>
              ) : childProfiles.length === 0 ? (
                <Card padding="lg">
                  <div className="text-center py-8">
                    <p className="text-gray-600">{t('dashboard:family.children.noChildren')}</p>
                  </div>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {childProfiles.map((profile) => (
                    <Card key={profile.id} padding="lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{profile.name}</h3>
                          <p className="text-sm text-gray-600">
                            {t('dashboard:family.children.preferred')}: {profile.preferredName}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                          {(() => {
                            const age = calculateAgeWithMonths(new Date(profile.birthDate));
                            return `${age.years}${t('dashboard:family.children.years')}${age.months}${t('dashboard:family.children.months')}`;
                          })()}
                        </span>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="text-sm">
                          <span className="text-gray-500">
                            {t('dashboard:family.children.birthday')}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {new Date(profile.birthDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                            })}
                          </span>
                        </div>

                        {profile.notes && (
                          <div className="text-sm">
                            <span className="text-gray-500">
                              {t('dashboard:family.children.specialNeeds')}:
                            </span>
                            <span className="ml-2 text-gray-900 whitespace-pre-wrap">
                              {profile.notes}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditChildProfile(profile)}
                        >
                          {t('dashboard:family.children.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteChildProfile(profile.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          {t('dashboard:family.children.delete')}
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Adult Profiles Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('dashboard:family.adults.title')}
                </h2>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setEditingAdultProfile(null);
                    setShowAdultForm(true);
                  }}
                >
                  + {t('dashboard:family.adults.addAdult')}
                </Button>
              </div>

              {loading ? (
                <Card padding="lg">
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('dashboard:loading')}</p>
                  </div>
                </Card>
              ) : adultProfiles.length === 0 ? (
                <Card padding="lg">
                  <div className="text-center py-8">
                    <p className="text-gray-600">{t('dashboard:family.adults.noAdults')}</p>
                  </div>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {adultProfiles.map((profile) => (
                    <Card key={profile.id} padding="lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{profile.name}</h3>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="text-sm">
                          <span className="text-gray-500">
                            {t('dashboard:family.adults.phone')}:
                          </span>
                          <span className="ml-2 text-gray-900">{profile.phone}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditAdultProfile(profile)}
                        >
                          {t('dashboard:family.adults.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteAdultProfile(profile.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          {t('dashboard:family.adults.delete')}
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Passes Tab Content */}
        {activeTab === 'passes' && (
          <div>
            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                {successMessage}
              </div>
            )}

            {passesLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">{t('dashboard:loading')}</p>
              </div>
            ) : passes.length === 0 ? (
              <Card padding="lg" className="text-center py-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('pass:dashboard.noPasses')}
                </h2>
                <p className="text-gray-600 mb-6">{t('pass:dashboard.noPassesDescription')}</p>
                <Button variant="primary" onClick={() => navigate('/activities')}>
                  {t('pass:dashboard.browsePasses')}
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {passes.map((userPass) => {
                  const passTitle = localize(
                    userPass.pass.titleCN,
                    userPass.pass.titleEN,
                    i18n.language
                  );
                  const activityTitle = localize(
                    userPass.pass.activity.titleCN,
                    userPass.pass.activity.titleEN,
                    i18n.language
                  );
                  const coverImage =
                    userPass.pass.activity.images?.find((img) => img.isCover)?.imageUrl ||
                    userPass.pass.activity.images?.[0]?.imageUrl;
                  const expiresDate = new Date(userPass.expiresAt).toLocaleDateString(
                    dateLocale(i18n.language),
                    { year: 'numeric', month: 'long', day: 'numeric', timeZone: DEFAULT_TIMEZONE }
                  );
                  const purchasedDate = new Date(userPass.purchasedAt).toLocaleDateString(
                    dateLocale(i18n.language),
                    { year: 'numeric', month: 'long', day: 'numeric', timeZone: DEFAULT_TIMEZONE }
                  );
                  const progressPercent = Math.round(
                    (userPass.usedSessions / userPass.pass.totalSessions) * 100
                  );
                  const isActive = userPass.displayStatus === 'active';
                  const canRefund =
                    isActive &&
                    userPass.usedSessions === 0 &&
                    new Date(userPass.expiresAt) > new Date();

                  const statusBadgeMap: Record<string, { label: string; className: string }> = {
                    active: {
                      label: t('pass:dashboard.status.active'),
                      className: 'bg-primary-pale text-primary-dark',
                    },
                    used_up: {
                      label: t('pass:dashboard.status.used_up'),
                      className: 'bg-gray-100 text-gray-600',
                    },
                    expired: {
                      label: t('pass:dashboard.status.expired'),
                      className: 'bg-brown-pale text-brown-dark',
                    },
                    refunded: {
                      label: t('pass:dashboard.status.refunded'),
                      className: 'bg-yellow-pale text-yellow-dark',
                    },
                  };
                  const badge = statusBadgeMap[userPass.displayStatus] || {
                    label: userPass.displayStatus,
                    className: 'bg-gray-100 text-gray-600',
                  };

                  return (
                    <Card key={userPass.id} padding="lg" className={!isActive ? 'opacity-75' : ''}>
                      <div className="flex flex-col md:flex-row md:items-start gap-6">
                        {/* Activity Image */}
                        <div className="flex-shrink-0 w-full md:w-32 h-32 bg-gray-200 rounded-lg overflow-hidden">
                          {coverImage ? (
                            <img
                              src={coverImage}
                              alt={passTitle}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                              <span className="text-gray-500">No image</span>
                            </div>
                          )}
                        </div>

                        {/* Pass Details */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{passTitle}</h3>
                              <p className="text-sm text-gray-500">{activityTitle}</p>
                            </div>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">
                                {t('pass:dashboard.progress', {
                                  used: userPass.usedSessions,
                                  total: userPass.pass.totalSessions,
                                })}
                              </span>
                              <span className="text-gray-500">
                                {t('pass:dashboard.remaining', {
                                  count: userPass.remainingSessions,
                                })}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-primary rounded-full h-2 transition-all"
                                style={{ width: `${progressPercent}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Dates */}
                          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                            <div>
                              <span className="text-gray-500">
                                {t('pass:dashboard.expiresOn', { date: expiresDate })}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                {t('pass:dashboard.purchasedOn', { date: purchasedDate })}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2">
                            {isActive && userPass.remainingSessions > 0 && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => navigate(`/activity/${userPass.pass.activity.slug}`)}
                              >
                                {t('pass:dashboard.actions.book')}
                              </Button>
                            )}
                            {canRefund && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRefundTargetPass(userPass)}
                                className="text-red-600 hover:text-red-700"
                              >
                                {t('pass:dashboard.actions.refund')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Pass Refund Confirmation Modal */}
        {refundTargetPass && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('pass:dashboard.refundModal.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('pass:dashboard.refundModal.message', {
                  amount: (refundTargetPass.paymentAmount / 100).toFixed(2),
                })}
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRefundTargetPass(null)}
                  disabled={refunding}
                >
                  {t('pass:dashboard.refundModal.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handlePassRefund}
                  disabled={refunding}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {refunding
                    ? t('pass:dashboard.refundModal.processing')
                    : t('pass:dashboard.refundModal.confirm')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {showChildForm && (
          <ChildProfileFormModal profile={editingChildProfile} onClose={handleCloseChildForm} />
        )}

        {showAdultForm && (
          <AdultProfileFormModal profile={editingAdultProfile} onClose={handleCloseAdultForm} />
        )}

        {showConsentForm && (
          <ConsentFormModal
            currentConsents={consents}
            consentType={consentFormType}
            onClose={() => setShowConsentForm(false)}
            onSubmit={handleConsentSubmit}
          />
        )}

        {cancelBookingTarget && (
          <CancelBookingModal
            booking={cancelBookingTarget}
            onClose={() => setCancelBookingTarget(null)}
            onConfirm={handleCancelConfirm}
          />
        )}

        {transferBooking && (
          <TransferConfirmationModal
            booking={transferBooking}
            onClose={() => setTransferBooking(null)}
            onConfirm={handleTransferConfirm}
          />
        )}

        {writeReviewActivityId && (
          <ReviewEditorModal
            activityId={writeReviewActivityId}
            onClose={() => setWriteReviewActivityId(null)}
            onSaved={() => {
              setWriteReviewActivityId(null);
              setSuccessMessage(t('review:notice.created'));
              loadMyReviews();
            }}
          />
        )}
      </div>
    </div>
  );
}
